import React, { useState, useCallback } from 'react';
import { Camera, FileImage, Check, AlertCircle, ArrowRight, X, RefreshCw, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { value: 'expense', label: 'Expense/Receipt', description: 'Receipts, bills, expense documents' },
  { value: 'invoice', label: 'Vendor Invoice', description: 'Invoices from suppliers' },
  { value: 'timecard', label: 'Timecard/Timesheet', description: 'Employee time records' },
  { value: 'receipt', label: 'Receipt', description: 'Store receipts' },
];

const OCRScanner = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [docType, setDocType] = useState('expense');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [processResult, setProcessResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setError(null);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      const ext = droppedFile.name.split('.').pop()?.toLowerCase();
      if (['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
        setFile(droppedFile);
        setPreview(URL.createObjectURL(droppedFile));
        setError(null);
      } else {
        setError('Please upload a JPG, PNG, or WebP image');
      }
    }
  }, []);

  const handleScan = async () => {
    if (!file) return;

    setIsScanning(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);

      const response = await fetch(`${API_URL}/api/ocr/scan`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Scan failed');
      }

      const data = await response.json();
      setScanResult(data);
      setEditedData(data.extracted_data || {});
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsScanning(false);
    }
  };

  const handleProcess = async () => {
    if (!scanResult) return;

    setIsScanning(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/ocr/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          scan_id: scanResult.scan_id,
          confirmed_data: editedData,
          target_type: docType === 'receipt' ? 'expense' : docType,
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Processing failed');
      }

      const data = await response.json();
      setProcessResult(data);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsScanning(false);
    }
  };

  const resetScanner = () => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setScanResult(null);
    setEditedData({});
    setProcessResult(null);
    setError(null);
  };

  const updateField = (key, value) => {
    setEditedData(prev => ({ ...prev, [key]: value }));
  };

  const renderDataEditor = () => {
    if (!editedData || Object.keys(editedData).length === 0) {
      return <p className="text-gray-500">No data extracted. Please try with a clearer image.</p>;
    }

    const fields = Object.entries(editedData).filter(([key]) => 
      !['entries', 'line_items', 'items'].includes(key)
    );

    return (
      <div className="space-y-3">
        {fields.map(([key, value]) => (
          <div key={key} className="grid grid-cols-3 gap-2 items-center">
            <Label className="text-sm text-gray-600 capitalize">
              {key.replace(/_/g, ' ')}
            </Label>
            <Input
              className="col-span-2"
              value={typeof value === 'object' ? JSON.stringify(value) : value || ''}
              onChange={(e) => updateField(key, e.target.value)}
            />
          </div>
        ))}

        {/* Handle line items/entries */}
        {editedData.entries && (
          <div className="mt-4">
            <Label className="text-sm font-medium">Time Entries</Label>
            <div className="mt-2 border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Regular Hrs</th>
                    <th className="px-3 py-2 text-left">OT Hrs</th>
                  </tr>
                </thead>
                <tbody>
                  {editedData.entries.map((entry, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2">{entry.date}</td>
                      <td className="px-3 py-2">{entry.hours_regular}</td>
                      <td className="px-3 py-2">{entry.hours_overtime}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {editedData.line_items && (
          <div className="mt-4">
            <Label className="text-sm font-medium">Line Items</Label>
            <div className="mt-2 border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">Description</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-right">Price</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {editedData.line_items.map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2">{item.description}</td>
                      <td className="px-3 py-2 text-right">{item.quantity}</td>
                      <td className="px-3 py-2 text-right">${item.unit_price}</td>
                      <td className="px-3 py-2 text-right">${item.amount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="ocr-scanner-modal">
        <CardHeader className="border-b flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-sky-600" />
              OCR Document Scanner
            </CardTitle>
            <CardDescription>
              Scan invoices, receipts, or timecards using AI Vision
            </CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-ocr-modal">
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>

        <CardContent className="flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium">Document Type</Label>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger className="w-full mt-1" data-testid="doc-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  preview ? 'border-sky-400 bg-sky-50' : 'border-gray-300 hover:border-sky-400'
                }`}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                data-testid="image-dropzone"
              >
                {preview ? (
                  <div className="space-y-4">
                    <img 
                      src={preview} 
                      alt="Document preview" 
                      className="max-h-64 mx-auto rounded-lg shadow-md"
                    />
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-sm text-gray-600">{file?.name}</span>
                      <Button variant="ghost" size="sm" onClick={() => { setFile(null); setPreview(null); }}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <FileImage className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">Drag and drop your document image here</p>
                    <p className="text-sm text-gray-500 mb-4">Supports JPG, PNG, WebP</p>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="ocr-file-upload"
                    />
                    <Button asChild variant="outline">
                      <label htmlFor="ocr-file-upload" className="cursor-pointer" data-testid="ocr-file-select-btn">
                        <Upload className="w-4 h-4 mr-2" />
                        Select Image
                      </label>
                    </Button>
                  </>
                )}
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleScan}
                  disabled={!file || isScanning}
                  className="bg-sky-600 hover:bg-sky-700"
                  data-testid="scan-btn"
                >
                  {isScanning ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Scanning with AI...
                    </>
                  ) : (
                    <>
                      <Camera className="w-4 h-4 mr-2" />
                      Scan Document
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Review */}
          {step === 2 && scanResult && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Extracted Data</h3>
                  <p className="text-sm text-gray-500">Review and edit the extracted information</p>
                </div>
                <Badge 
                  className={scanResult.confidence >= 0.8 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}
                >
                  {Math.round(scanResult.confidence * 100)}% confidence
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Image preview */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Original Document</Label>
                  <img 
                    src={preview} 
                    alt="Document" 
                    className="w-full rounded-lg border shadow-sm"
                  />
                </div>

                {/* Data editor */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Extracted Data</Label>
                  <div className="border rounded-lg p-4 max-h-96 overflow-auto">
                    {renderDataEditor()}
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={resetScanner}>
                  Scan Another
                </Button>
                <Button
                  onClick={handleProcess}
                  disabled={isScanning}
                  className="bg-sky-600 hover:bg-sky-700"
                  data-testid="process-ocr-btn"
                >
                  {isScanning ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Creating Record...
                    </>
                  ) : (
                    <>
                      Create Record
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Complete */}
          {step === 3 && processResult && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Document Processed!</h3>
              <p className="text-gray-600 mb-6">{processResult.message}</p>

              <div className="bg-gray-50 rounded-lg p-4 max-w-sm mx-auto mb-6">
                <p className="text-sm text-gray-600">
                  Record Type: <span className="font-medium">{processResult.type}</span>
                </p>
                {processResult.record_id && (
                  <p className="text-sm text-gray-600">
                    Record ID: <span className="font-mono text-xs">{processResult.record_id}</span>
                  </p>
                )}
                {processResult.entries_created && (
                  <p className="text-sm text-gray-600">
                    Entries Created: <span className="font-medium">{processResult.entries_created}</span>
                  </p>
                )}
              </div>

              <div className="flex justify-center gap-4">
                <Button variant="outline" onClick={resetScanner}>
                  Scan Another Document
                </Button>
                <Button onClick={onClose} className="bg-sky-600 hover:bg-sky-700">
                  Done
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OCRScanner;
